import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { ArrowLeft, Loader2, FileSignature, CheckCircle2, CreditCard, AlertTriangle, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONDITIONS = [
  "La présence DJ est assurée jusqu'à 4 H du matin.",
  "Un acompte de 30 % est versé à la signature du devis et du contrat ; le solde est réglable en ligne avant l'événement.",
  "Les demandes musicales via la playlist collaborative sont clôturées 48 h avant l'événement.",
  "Discours, surprises et diaporamas sont transmis au plus tard 48 h avant l'événement.",
  "La coordination des surprises s'effectue via deux contacts référents désignés par les clients.",
  "Les options (étincelles froides, fumée lourde, éclairage architectural…) font l'objet d'un devis dédié.",
  "La souscription d'une assurance annulation et intempéries est recommandée.",
];

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-cyan-500/10 py-2.5 last:border-0">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="text-sm font-semibold text-slate-100">{value}</span>
  </div>
);

export default function SignaturePage() {
  const { token } = useParams();
  const [contrat, setContrat] = useState(null);
  const [error, setError] = useState(null);
  const [nom, setNom] = useState("");
  const [accepte, setAccepte] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/contrats/${token}`)
      .then((r) => {
        setContrat(r.data);
        if (r.data.statut === "signe") {
          setSigned({ lookup_key: r.data.lookup_key, acompte: r.data.acompte });
        }
      })
      .catch(() => setError("Ce lien de signature est invalide ou a expiré."));
  }, [token]);

  const signer = async (e) => {
    e.preventDefault();
    setSigning(true);
    try {
      const { data } = await axios.post(`${API}/contrats/${token}/signer`, { nom, accepte });
      setSigned(data);
      toast.success("Contrat signé", { description: "Il ne reste que l'acompte pour réserver votre date." });
    } catch (err) {
      toast.error("Signature impossible", { description: err.response?.data?.detail || "Réessayez." });
    } finally {
      setSigning(false);
    }
  };

  const payer = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: signed.lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Le paiement n'a pas pu être initialisé", { description: "Réessayez dans un instant." });
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080C14]" data-testid="signature-page">
      <header className="border-b border-cyan-500/15 bg-[#080C14]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="signature-back-link"
            className="group flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300"
          >
            <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Retour au site
          </Link>
          <img src="/logo-v2.png" alt="KLMProd" className="h-8 w-auto" />
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-4 py-14 sm:px-6">
        {!contrat && !error && (
          <div className="flex h-64 items-center justify-center" data-testid="signature-loading">
            <Loader2 size={26} className="animate-spin text-cyan-400" />
          </div>
        )}

        {error && (
          <div className="rounded-3xl border border-amber-400/25 bg-[#0E1524]/80 p-10 text-center" data-testid="signature-error">
            <AlertTriangle size={36} className="mx-auto text-amber-400" />
            <h1 className="font-display mt-4 text-2xl font-extrabold text-slate-50">Lien invalide</h1>
            <p className="mt-2 text-sm text-slate-400">{error}</p>
          </div>
        )}

        {contrat && !signed && (
          <div className="rounded-3xl border border-cyan-500/20 bg-[#0E1524]/80 p-8 backdrop-blur-xl sm:p-10" data-testid="signature-contract">
            <p className="font-mono-brand flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-cyan-400">
              <FileSignature size={14} /> Contrat de prestation
            </p>
            <h1 className="font-display mt-3 text-3xl font-extrabold text-slate-50">
              {contrat.type_evenement} — Formule {contrat.formule}
            </h1>

            <div className="mt-6 rounded-2xl border border-cyan-500/10 bg-[#080C14]/60 px-5 py-2">
              <Row label="Client" value={contrat.client_nom} />
              <Row
                label="Date de l'événement"
                value={
                  contrat.date_evenement
                    ? format(parseISO(contrat.date_evenement), "d MMMM yyyy", { locale: fr })
                    : "À confirmer"
                }
              />
              <Row label="Invités estimés" value={`${contrat.invites}`} />
              <Row label="Montant total" value={`${contrat.montant_total.toLocaleString("fr-FR")} €`} />
              <Row label="Acompte à la signature (30 %)" value={`${contrat.acompte.toLocaleString("fr-FR")} €`} />
            </div>

            <h2 className="font-display mt-7 text-sm font-bold uppercase tracking-wider text-slate-300">
              Conditions de la prestation
            </h2>
            <ul className="mt-3 space-y-2">
              {CONDITIONS.map((c) => (
                <li key={c.slice(0, 30)} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-400">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-cyan-400" />
                  {c}
                </li>
              ))}
            </ul>

            <form onSubmit={signer} className="mt-8 space-y-5" data-testid="signature-form">
              <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-cyan-500/15 bg-[#080C14]/60 p-4">
                <input
                  type="checkbox"
                  checked={accepte}
                  onChange={(e) => setAccepte(e.target.checked)}
                  data-testid="signature-accept-checkbox"
                  className="mt-1 h-4 w-4 accent-cyan-400"
                />
                <span className="text-sm leading-relaxed text-slate-300">
                  J'ai lu et j'accepte les conditions de la prestation. Je comprends que ma signature
                  électronique simple vaut engagement (règlement eIDAS).
                </span>
              </label>
              <div>
                <label className="text-sm text-slate-300" htmlFor="signature-nom">
                  Votre nom complet (vaut signature) *
                </label>
                <input
                  id="signature-nom"
                  required
                  minLength={3}
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  placeholder="Ex. Camille Martin"
                  data-testid="signature-name-input"
                  className="mt-2 w-full rounded-xl border border-cyan-500/20 bg-[#080C14]/70 px-4 py-3 font-display text-lg italic text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                />
              </div>
              <button
                type="submit"
                disabled={signing || !accepte || nom.trim().length < 3}
                data-testid="signature-submit-button"
                className="bg-brand-gradient flex w-full items-center justify-center gap-2 rounded-full py-4 text-sm font-bold text-[#04121f] shadow-[0_0_40px_rgba(0,150,255,0.25)] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-50"
              >
                {signing ? <Loader2 size={16} className="animate-spin" /> : <FileSignature size={16} />}
                {signing ? "Signature en cours…" : "Signer le contrat"}
              </button>
            </form>
          </div>
        )}

        {contrat && signed && (
          <div className="rounded-3xl border border-emerald-400/25 bg-[#0E1524]/80 p-10 text-center backdrop-blur-xl" data-testid="signature-success">
            <CheckCircle2 size={44} className="mx-auto text-emerald-400" />
            <h1 className="font-display mt-5 text-3xl font-extrabold text-slate-50">Contrat signé !</h1>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-400">
              Merci ! Pour réserver définitivement votre date, il ne reste que le règlement de
              l'acompte de 30 %, soit{" "}
              <strong className="text-slate-100">{signed.acompte.toLocaleString("fr-FR")} €</strong>,
              par carte bancaire sécurisée.
            </p>
            <button
              onClick={payer}
              disabled={paying}
              data-testid="signature-pay-button"
              className="bg-brand-gradient mx-auto mt-8 flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-bold text-[#04121f] shadow-[0_0_40px_rgba(0,150,255,0.3)] transition-transform duration-300 hover:scale-105 disabled:opacity-60"
            >
              {paying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
              {paying ? "Redirection…" : `Payer l'acompte — ${signed.acompte.toLocaleString("fr-FR")} €`}
            </button>
            <p className="mt-4 text-xs text-slate-600">Paiement sécurisé par Stripe.</p>
            <a
              href={`${API}/contrats/${token}/pdf`}
              data-testid="signature-pdf-link"
              className="mt-5 inline-flex items-center gap-2 text-xs font-semibold text-cyan-300 transition-colors hover:text-cyan-200"
            >
              <Download size={13} /> Télécharger le devis-contrat en PDF
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
